// Site-Scope Editor — master admin picks a domain, sees every user,
// and edits their `site_scope` list (which sites they can see data for)
// and `training_role` (which learning path applies). Reuses the
// existing PATCH /api/admin/domains/{domain_id}/users/{user_id} route.
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Save, Globe, Building2, X } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRAINING_ROLES = [
  { key: '', label: 'Auto (derive from role)' },
  { key: 'membership_consultant', label: 'Membership Consultant' },
  { key: 'reception', label: 'Reception' },
  { key: 'club_manager', label: 'Club Manager' },
  { key: 'regional_leader', label: 'Regional / Program Leader' },
];

const AdminSiteScopePage = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [sites, setSites] = useState([]);           // unique labels
  const [users, setUsers] = useState([]);
  const [dirty, setDirty] = useState({});           // {user_id: {site_scope:[], training_role:''}}
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/domains`, { headers })
      .then(r => { setDomains(r.data || []); if (r.data?.length) setSelectedDomain(r.data[0].id); })
      .catch(() => toast.error('Failed to load domains'))
      .finally(() => setLoading(false));
  }, [headers]);

  useEffect(() => {
    if (!selectedDomain) return;
    setLoading(true);
    const d = domains.find(x => x.id === selectedDomain);
    const es = d?.extension_sites || {};
    setSites(Array.from(new Set(Object.values(es).map(s => String(s || '').trim()).filter(Boolean))).sort());
    axios.get(`${API}/admin/domains/${selectedDomain}/users`, { headers })
      .then(r => setUsers(r.data?.users || r.data || []))
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setLoading(false));
  }, [selectedDomain, domains, headers]);

  const getScope = (u) => (dirty[u.id]?.site_scope ?? u.site_scope) || [];
  const getTrainingRole = (u) => (dirty[u.id]?.training_role ?? u.training_role) || '';
  const toggleSite = (u, site) => {
    const cur = getScope(u);
    const next = cur.includes(site) ? cur.filter(s => s !== site) : [...cur, site];
    setDirty(d => ({ ...d, [u.id]: { ...d[u.id], site_scope: next } }));
  };
  const setRole = (u, val) => setDirty(d => ({ ...d, [u.id]: { ...d[u.id], training_role: val } }));

  const save = async (u) => {
    const patch = dirty[u.id] || {};
    if (Object.keys(patch).length === 0) return;
    setSaving(u.id);
    try {
      await axios.patch(
        `${API}/admin/domains/${selectedDomain}/users/${u.id}`,
        patch,
        { headers },
      );
      toast.success(`Saved for ${u.name || u.email}`);
      // Refresh this user's row in the local list.
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, ...patch } : x));
      setDirty(d => { const n = { ...d }; delete n[u.id]; return n; });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally { setSaving(null); }
  };

  const scopedUsers = users.filter(u => ['domain_supervisor','supervisor','domain_admin','office_manager'].includes(u.role));

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="site-scope-page">
        <div>
          <h1 className="text-2xl font-bold text-white">Site-Scope Editor</h1>
          <p className="text-white/40 text-sm mt-0.5">
            Restrict what a supervisor sees. Empty scope = see every site in the domain. Set specific sites to lock a supervisor to just those.
          </p>
        </div>

        {/* Domain picker */}
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-white/40 text-xs font-medium uppercase tracking-wide">Domain</p>
          {domains.map(d => (
            <button
              key={d.id}
              onClick={() => setSelectedDomain(d.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm ${
                selectedDomain === d.id ? 'bg-teal-500/20 border border-teal-500/40 text-teal-300' : 'bg-white/5 border border-white/10 text-white/50 hover:text-white/70'
              }`}
              data-testid={`sitescope-domain-${d.id}`}
            >
              <Building2 className="w-3 h-3" />
              {d.company?.name || d.yabbit_domain}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-white/30 animate-spin" /></div>
        ) : sites.length === 0 ? (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 text-amber-200 text-sm">
            No sites configured on this domain yet. Set up the extension → site map on the Domain settings first.
          </div>
        ) : (
          <>
            {/* Legend */}
            <div className="text-xs text-white/50 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3" /> Available sites:</span>
              {sites.map(s => <span key={s} className="tf-badge-forest bg-white/5 border border-white/10 text-white/70 px-2 py-0.5 rounded-full">{s}</span>)}
            </div>

            {/* Users */}
            <div className="bg-white/[0.02] border border-white/10 rounded-xl overflow-hidden" data-testid="sitescope-users">
              {scopedUsers.length === 0 ? (
                <p className="text-center py-8 text-white/40 italic">No supervisors/admin in this domain yet.</p>
              ) : (
                <div className="divide-y divide-white/5">
                  {scopedUsers.map(u => {
                    const scope = getScope(u);
                    const role = getTrainingRole(u);
                    const isDirty = !!dirty[u.id];
                    return (
                      <div key={u.id} className="p-4" data-testid={`sitescope-user-${u.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div>
                            <p className="text-white font-semibold">{u.name || u.email}</p>
                            <p className="text-xs text-white/40">{u.email} · <span className="text-white/60">{u.role}</span></p>
                          </div>
                          <button
                            onClick={() => save(u)}
                            disabled={!isDirty || saving === u.id}
                            className="text-xs bg-teal-500 hover:bg-teal-400 disabled:opacity-30 text-white font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1"
                            data-testid={`sitescope-save-${u.id}`}
                          >
                            {saving === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            Save
                          </button>
                        </div>
                        {/* Site scope chips */}
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">Sites visible</span>
                          {sites.map(s => {
                            const on = scope.includes(s);
                            return (
                              <button
                                key={s}
                                onClick={() => toggleSite(u, s)}
                                className={`text-xs px-2.5 py-0.5 rounded-full border font-medium transition-colors ${
                                  on
                                    ? 'bg-teal-600 border-teal-700 text-white hover:bg-teal-500'
                                    : 'bg-white/5 border-white/10 text-white/50 hover:text-white/80'
                                }`}
                                data-testid={`sitescope-toggle-${u.id}-${s}`}
                              >
                                {s}{on && <X className="w-3 h-3 inline ml-1" />}
                              </button>
                            );
                          })}
                          {scope.length === 0 && <span className="text-[10px] italic text-amber-300">Empty = sees all sites in domain</span>}
                        </div>
                        {/* Training role picker */}
                        <div className="mt-3 flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">Training path</span>
                          <select
                            value={role}
                            onChange={e => setRole(u, e.target.value)}
                            className="bg-white/5 border border-white/10 text-white/80 text-xs rounded px-2 py-1"
                            data-testid={`sitescope-training-${u.id}`}
                          >
                            {TRAINING_ROLES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSiteScopePage;
